/**
 * Docs & databases (Notion-like): the page tree, databases (schema + rows + views) and the Loro document model
 * for page bodies. Property values and page metadata are server-authoritative (REST + events, last write wins per
 * cell); only page bodies are CRDTs (Loro, synced with PageDO over loro-protocol).
 *
 * Every row is also a page (kind "row", same id), so a row opens as a full page with a body.
 */
import { z } from "zod";
import { Id, Locale, Timestamp } from "./entities.ts";

// ---------- Page tree ----------

export const PageKind = z.enum(["page", "database", "row"]);
export type PageKind = z.infer<typeof PageKind>;

/** Sidebar sections: shared with the workspace, or private to the creator (Notion's Teamspace / Private). */
export const PageVisibility = z.enum(["workspace", "private"]);
export type PageVisibility = z.infer<typeof PageVisibility>;

/** Effective permission of the caller on a page (inherited down the tree; guests/agents only via AccessPolicy.pageIds). */
export const PageAccess = z.enum(["full", "edit", "comment", "view"]);
export type PageAccess = z.infer<typeof PageAccess>;

/**
 * What "Everyone at <workspace>" (full, unrestricted members) gets on a page subtree when nothing more specific
 * applies (sharing.ts); "none" = only people invited. Absent on every page above ⇒ "edit" (the workspace default).
 * Private sections ignore it (only their owner + people invited).
 */
export const GeneralAccess = z.enum(["full", "edit", "comment", "view", "none"]);
export type GeneralAccess = z.infer<typeof GeneralAccess>;

export const PageNode = z.object({
  id: Id,
  kind: PageKind,
  /** Absent ⇒ top level of its section. Rows: absent (their parent is `databaseId`). */
  parentId: Id.optional(),
  /** Rows only: the database the row belongs to. */
  databaseId: Id.optional(),
  title: z.string().max(2000),
  /** Page icon: an emoji (content, like Notion) or an icon-font name prefixed "icon:" (e.g. "icon:file-text"). */
  icon: z.string().max(64).optional(),
  visibility: PageVisibility,
  /** Fractional index (fractional-indexing) among siblings; sort by plain string comparison. */
  rank: z.string(),
  myAccess: PageAccess,
  /** Has child pages/databases (lets the sidebar show a disclosure triangle before children load). */
  hasChildren: z.boolean().optional(),
  createdBy: Id,
  createdAt: Timestamp,
  updatedBy: Id,
  updatedAt: Timestamp,
  /** In trash (restorable); omitted from bootstrap/listPages unless ?archived=true. */
  archivedAt: Timestamp.optional(),
  /** This page's own general access (sharing.ts); absent ⇒ inherited from the page above (top: workspace default). */
  generalAccess: GeneralAccess.optional(),
  /** Number of people with an explicit share on this page itself (sharing.ts); absent ⇒ none. */
  sharedWith: z.number().int().nonnegative().optional(),
  /** Published to the web: an active public link on this page (sharing.ts). */
  published: z.boolean().optional(),
  /** Locked: body, title and icon are read-only for everyone until someone with edit access unlocks it. */
  locked: z.boolean().optional(),
  /**
   * Template content (templates.ts): the id of the custom template or row template this node belongs to. Never part
   * of the page tree; opened only to edit the template ("editing template" mode).
   */
  templateId: Id.optional(),
});
export type PageNode = z.infer<typeof PageNode>;

// ---------- Databases ----------

export const PropertyType = z.enum([
  "title", "text", "number", "select", "multi_select", "status", "date", "person", "checkbox",
  "url", "email", "files", "relation", "created_time", "created_by", "edited_time", "edited_by", "formula", "rollup",
  /** Mail contacts (people outside the workspace you email, e.g. a project's Clients), not workspace members. */
  "contact",
]);
export type PropertyType = z.infer<typeof PropertyType>;

/** Option colours map to tokens `tag.<color>` (bg/fg pairs) on every platform. */
export const OptionColor = z.enum(["default", "gray", "brown", "orange", "yellow", "green", "blue", "purple", "pink", "red"]);
export type OptionColor = z.infer<typeof OptionColor>;

export const SelectOption = z.object({ id: Id, name: z.string().min(1).max(100), color: OptionColor });
export type SelectOption = z.infer<typeof SelectOption>;

/** Status options are grouped like Notion: To-do / In progress / Complete (drives "done" styling and progress). */
export const StatusGroup = z.enum(["todo", "in_progress", "complete"]);
export type StatusGroup = z.infer<typeof StatusGroup>;
export const StatusOption = SelectOption.extend({ group: StatusGroup });
export type StatusOption = z.infer<typeof StatusOption>;

/**
 * Column calculations (the table's Calculate row, Notion) and rollup aggregates. Which ones a property offers depends on
 * its type (core `calculationsFor`): counts and percents for every type; sum…range for numbers; earliest/latest/
 * date_range for dates; checked… for checkboxes. Formula and rollup columns offer those of their result kind.
 */
export const Calculation = z.enum([
  "count_all", "count_values", "count_unique", "count_empty", "count_not_empty", "percent_empty", "percent_not_empty",
  "sum", "average", "median", "min", "max", "range", "earliest", "latest", "date_range",
  "checked", "unchecked", "percent_checked", "percent_unchecked",
]);
export type Calculation = z.infer<typeof Calculation>;

/** The kind of value a formula or rollup produces (decides filter operators, sorting, grouping and calculations). */
export const FormulaResultKind = z.enum(["number", "text", "boolean", "date"]);
export type FormulaResultKind = z.infer<typeof FormulaResultKind>;

/** Spreadsheet error codes, as Google Sheets shows them. */
export const FormulaErrorCode = z.enum(["#ERROR!", "#DIV/0!", "#VALUE!", "#REF!", "#NAME?", "#N/A", "#NUM!"]);
export type FormulaErrorCode = z.infer<typeof FormulaErrorCode>;

/**
 * A formula property (docs/specs/database-formulas.md). `expression` is stored with stable references:
 * `{propId}` = this row's cell, `{rowId:propId}` = one cell, `{rowId:propId..rowId:propId}` = a range,
 * `{*:propId}` = a whole column. Clients show it as typed text with core `formulaDisplayText` (A1 letters follow the
 * database's property order, row numbers its manual order) and send typed text back; the server turns it into the
 * stored form on save. Broken formulas can be saved (cells show the error), as in Sheets.
 */
export const FormulaDef = z.object({
  expression: z.string().max(8000),
  /** Number display: absent ⇒ as the engine infers (dates stay dates). */
  format: z.enum(["number", "percent", "currency", "date"]).optional(),
  /** Set by the server from the results (absent until first calculated, or when every row is empty or an error). */
  resultKind: FormulaResultKind.optional(),
});
export type FormulaDef = z.infer<typeof FormulaDef>;

/** A rollup: `calculate` over the `targetPropertyId` cells of the rows linked through `relationPropertyId`. */
export const RollupDef = z.object({
  relationPropertyId: Id,
  targetPropertyId: Id,
  calculate: z.union([Calculation, z.literal("show_original")]),
  /** Set by the server (see FormulaDef.resultKind). */
  resultKind: FormulaResultKind.optional(),
});
export type RollupDef = z.infer<typeof RollupDef>;

export const PropertyDef = z.object({
  id: Id,
  name: z.string().min(1).max(100),
  type: PropertyType,
  /** select / multi_select. */
  options: z.array(SelectOption).optional(),
  /** status. */
  statusOptions: z.array(StatusOption).optional(),
  /** number. */
  numberFormat: z.enum(["number", "percent", "currency"]).optional(),
  /** relation: target database (two-way relations store the inverse property id). */
  relation: z.object({ databaseId: Id, inversePropertyId: Id.optional() }).optional(),
  /** formula. */
  formula: FormulaDef.optional(),
  /** rollup. */
  rollup: RollupDef.optional(),
});
export type PropertyDef = z.infer<typeof PropertyDef>;

/** Calendar date (all-day) "YYYY-MM-DD" or ISO date-time with offset; `end` makes it a range. */
export const DateValue = z.object({ start: z.string(), end: z.string().optional() });
export type DateValue = z.infer<typeof DateValue>;

/** A calculated value. An empty result (empty text, no rows) is stored as an absent cell. */
export const FormulaResult = z.discriminatedUnion("kind", [
  z.object({ kind: z.literal("number"), number: z.number() }),
  z.object({ kind: z.literal("text"), text: z.string() }),
  z.object({ kind: z.literal("boolean"), value: z.boolean() }),
  z.object({ kind: z.literal("date"), date: DateValue }),
  z.object({ kind: z.literal("error"), error: FormulaErrorCode, message: z.string().max(500).optional() }),
]);
export type FormulaResult = z.infer<typeof FormulaResult>;

/** A cell. Empty cells are omitted from `Row.values` (never null). Computed types are filled in by the server. */
export const PropertyValue = z.discriminatedUnion("type", [
  z.object({ type: z.literal("title"), text: z.string().max(2000) }),
  z.object({ type: z.literal("text"), text: z.string().max(10_000) }),
  z.object({ type: z.literal("number"), number: z.number() }),
  z.object({ type: z.literal("select"), optionId: Id }),
  z.object({ type: z.literal("multi_select"), optionIds: z.array(Id) }),
  z.object({ type: z.literal("status"), optionId: Id }),
  z.object({ type: z.literal("date"), date: DateValue }),
  z.object({ type: z.literal("person"), userIds: z.array(Id) }),
  z.object({ type: z.literal("checkbox"), checked: z.boolean() }),
  z.object({ type: z.literal("url"), url: z.string().max(2000) }),
  z.object({ type: z.literal("email"), email: z.string().max(320) }),
  z.object({ type: z.literal("files"), fileIds: z.array(Id) }),
  z.object({ type: z.literal("relation"), pageIds: z.array(Id) }),
  /** Mail contact ids (MailContact.id), in the order they were added. */
  z.object({ type: z.literal("contact"), contactIds: z.array(Id) }),
  /** Computed by the server (read-only): a formula's result. */
  z.object({ type: z.literal("formula"), result: FormulaResult }),
  /** Computed by the server (read-only): a rollup's result; `show_original` lists the related cells as text. */
  z.object({ type: z.literal("rollup"), result: FormulaResult }),
  /**
   * A formula in one cell of an editable property (Sheet view; docs/specs/database-formulas.md § Phase 2).
   * `expression` is the stored form (stable references, `$` marks an absolute part); `result` is set by the server
   * and fits the column's type (core `cellFormulaResult`), else it's a #VALUE! error. Written through
   * POST /databases/:id/cells, never through row updates.
   */
  z.object({ type: z.literal("cell_formula"), expression: z.string().max(8000), result: FormulaResult }),
]);
export type PropertyValue = z.infer<typeof PropertyValue>;

/**
 * A row template (Notion's database templates): default cells for new rows plus a pre-filled body. The id is also the
 * id of the hidden page holding the body (open it to edit the template; its title is the template's name).
 */
export const RowTemplate = z.object({
  id: Id,
  databaseId: Id,
  name: z.string().max(2000),
  icon: z.string().max(64).optional(),
  /** propertyId → default value (computed properties never; stale cells are ignored when applied). */
  values: z.record(Id, PropertyValue),
  /** Order in the "New ▾" menu (fractional index). */
  rank: z.string(),
  createdBy: Id,
  updatedAt: Timestamp,
});
export type RowTemplate = z.infer<typeof RowTemplate>;

/**
 * A cell of a summary row: plain `text`, or a formula — `expression` (stored form) with its server-set `result`.
 * Exactly one of `text` / `expression` is present.
 */
export const SummaryCell = z.object({
  text: z.string().max(10_000).optional(),
  expression: z.string().max(8000).optional(),
  result: FormulaResult.optional(),
});
export type SummaryCell = z.infer<typeof SummaryCell>;

/**
 * A row under the data in every Sheet view (totals and notes), numbered after the last data row. Belongs to the
 * database, not to a view. Cells are untyped (text or a formula).
 */
export const SummaryRow = z.object({ id: Id, cells: z.record(Id, SummaryCell) });
export type SummaryRow = z.infer<typeof SummaryRow>;

export const Database = z.object({
  id: Id, // same id as its PageNode (kind "database")
  title: z.string().max(2000),
  icon: z.string().max(64).optional(),
  /** Exactly one property has type "title". Order = default column order. */
  properties: z.array(PropertyDef),
  updatedAt: Timestamp,
  /** Row templates in menu order (absent when there are none, and from older servers). */
  rowTemplates: z.array(RowTemplate).optional(),
  /** The row template "New" uses (absent ⇒ an empty row). */
  defaultRowTemplateId: Id.optional(),
  /** Sheet view summary rows, in order, after the data rows (absent when there are none). At most 200. */
  summaryRows: z.array(SummaryRow).max(200).optional(),
});
export type Database = z.infer<typeof Database>;

export const Row = z.object({
  id: Id, // same id as its PageNode (kind "row")
  databaseId: Id,
  /** Manual order (board cards within a column, table rows when unsorted). */
  rank: z.string(),
  /** propertyId → value. created_time/by and edited_time/by are derived from the fields below, not stored here. */
  values: z.record(Id, PropertyValue),
  icon: z.string().max(64).optional(),
  createdBy: Id,
  createdAt: Timestamp,
  updatedBy: Id,
  updatedAt: Timestamp,
});
export type Row = z.infer<typeof Row>;

// ---------- Views ----------

export const ViewLayout = z.enum(["board", "table", "list", "calendar", "sheet"]);
export type ViewLayout = z.infer<typeof ViewLayout>;

export const FilterOperator = z.enum([
  "eq", "neq", "contains", "not_contains", "starts_with", "ends_with", "is_empty", "is_not_empty",
  "gt", "gte", "lt", "lte", "before", "after", "on_or_before", "on_or_after", "past_week", "next_week",
  "has_any", "has_all", "has_none", "is_me",
]);
export type FilterOperator = z.infer<typeof FilterOperator>;

export const FilterCondition = z.object({
  propertyId: Id,
  operator: FilterOperator,
  /** Text/number/date string/option ids/user ids/boolean depending on the property type; absent for unary operators. */
  value: z.union([z.string(), z.number(), z.boolean(), z.array(z.string())]).optional(),
});
export type FilterCondition = z.infer<typeof FilterCondition>;

export const ViewFilter = z.object({ combinator: z.enum(["and", "or"]), conditions: z.array(FilterCondition).max(50) });
export type ViewFilter = z.infer<typeof ViewFilter>;

export const ViewSort = z.object({ propertyId: Id, direction: z.enum(["asc", "desc"]) });
export type ViewSort = z.infer<typeof ViewSort>;

export const SheetSettings = z.object({
  freeze: z.object({ rows: z.number().int().min(0).max(100), columns: z.number().int().min(0).max(20) }),
  widths: z.record(Id, z.number().int().min(40).max(1200)).optional(),
});
export type SheetSettings = z.infer<typeof SheetSettings>;

export const DbView = z.object({
  id: Id,
  databaseId: Id,
  name: z.string().min(1).max(100),
  layout: ViewLayout,
  rank: z.string(),
  filter: ViewFilter.optional(),
  sorts: z.array(ViewSort).max(10),
  /** board (required) / table / list: group rows by a select, status or person property. */
  groupByPropertyId: Id.optional(),
  /** Group keys (option id, user id, or "none") hidden in this view. */
  hiddenGroups: z.array(z.string()).optional(),
  /** calendar (required): the date property that places rows on the calendar. */
  datePropertyId: Id.optional(),
  /** Properties shown on cards/rows, in order (table columns; board card fields). Title is always shown. */
  visiblePropertyIds: z.array(Id),
  /** table: column widths in points, by property id. */
  columnWidths: z.record(Id, z.number().int().min(40).max(1200)).optional(),
  /** table: the Calculate row (property id → calculation), over the rows the view shows. */
  calculations: z.record(Id, Calculation).optional(),
  /** table: show row numbers and column letters (the A1 grid formulas use). */
  showRowNumbers: z.boolean().optional(),
  /** sheet: frozen data rows under the always-frozen header, frozen columns (default 1), column widths by property id. */
  sheet: SheetSettings.optional(),
});
export type DbView = z.infer<typeof DbView>;

// ---------- Page bodies (Loro document model) ----------

/**
 * A page body is a LoroDoc:
 *   doc.getTree("blocks")  — MovableTree; each node is a block (children = nested blocks, order = sibling order)
 *   node.data (LoroMap):   "type": BlockType, "props": JSON per type (below), "text": LoroText (rich text)
 * LoroText marks: bold, italic, strike, code, underline (boolean); link {url}; color {color: OptionColor};
 * mention {kind: "user"|"page"|"date", id?: string, date?: string} (inline atoms are a U+FFFC char with the mark).
 * Server (PageDO) stores updates + full snapshots (history kept for time travel; shallow snapshots once page-history
 * retention exists); title is NOT in the body (it lives on PageNode).
 */
export const BlockType = z.enum([
  "paragraph", "heading1", "heading2", "heading3", "bulleted", "numbered", "todo", "toggle", "quote", "callout",
  "code", "divider", "image", "file", "embed", "child_page", "child_database", "link_to_page", "table",
]);
export type BlockType = z.infer<typeof BlockType>;

/** `props` per block type (all optional; absent ⇒ default). */
export const BlockProps = z.object({
  checked: z.boolean().optional(), // todo
  icon: z.string().max(64).optional(), // callout
  color: OptionColor.optional(), // callout / any block background
  language: z.string().max(40).optional(), // code
  fileId: Id.optional(), // image / file
  caption: z.string().max(2000).optional(), // image / file / embed
  url: z.string().max(2000).optional(), // embed
  pageId: Id.optional(), // child_page / child_database / link_to_page
  /** table: rows × cells of plain text (simple tables only in v1). */
  cells: z.array(z.array(z.string().max(2000))).optional(),
  hasHeaderRow: z.boolean().optional(),
});
export type BlockProps = z.infer<typeof BlockProps>;

// ---------- REST DTOs ----------

/**
 * Template reference for CreatePageRequest.template: a built-in template id (snake_case, e.g. "projects",
 * "bug_tracker"; the v1 values "blank" | "projects" | "meeting_notes" | "wiki" keep working) or "custom:<templateId>"
 * for a saved template (templates.ts). Was a closed enum; any string of this shape is accepted now (unknown ids are a
 * 400 from the server).
 */
export const PageTemplate = z.string().min(1).max(80).regex(/^(?:[a-z][a-z0-9_]*|custom:[A-Za-z0-9_-]+)$/);
export type PageTemplate = z.infer<typeof PageTemplate>;
/** Prefix of saved-template references ("custom:<templateId>"). */
export const CUSTOM_TEMPLATE_PREFIX = "custom:";
/** The built-in templates of v1 (still valid `PageTemplate` values). */
export const LEGACY_PAGE_TEMPLATES = ["blank", "projects", "meeting_notes", "wiki"] as const;

export const ListPagesQuery = z.object({ archived: z.coerce.boolean().optional() });
export const ListPagesResponse = z.object({ pages: z.array(PageNode), favoritePageIds: z.array(Id) });
export type ListPagesResponse = z.infer<typeof ListPagesResponse>;

export const CreatePageRequest = z.object({
  kind: z.enum(["page", "database"]),
  parentId: Id.optional(),
  visibility: PageVisibility.optional(),
  title: z.string().max(2000).optional(),
  icon: z.string().max(64).optional(),
  /**
   * A built-in template ("projects" creates Projects + Tasks databases with a relation and a Board default view) or
   * "custom:<templateId>" (a copy of a saved template). Templates decide the kind themselves (`kind` is ignored).
   */
  template: PageTemplate.optional(),
  /** Place after this sibling (default: last). */
  afterId: Id.optional(),
  clientId: z.string().max(64).optional(),
  /** Language of a built-in template's content (default: my profile language, else the workspace's, else "en"). */
  locale: Locale.optional(),
  /** Templates: leave the sample rows out ("Start empty"). */
  startEmpty: z.boolean().optional(),
});
export type CreatePageRequest = z.infer<typeof CreatePageRequest>;
/** `pages`: every page created (templates may create several); `page` is the one to open. */
export const CreatePageResponse = z.object({ page: PageNode, pages: z.array(PageNode), databases: z.array(Database), views: z.array(DbView) });
export type CreatePageResponse = z.infer<typeof CreatePageResponse>;

export const UpdatePageRequest = z.object({
  title: z.string().max(2000).optional(),
  icon: z.string().max(64).optional(),
  /** Move: new parent (null ⇒ top level) and/or position. */
  parentId: Id.nullable().optional(),
  afterId: Id.nullable().optional(),
  visibility: PageVisibility.optional(),
  /** true ⇒ move to trash (with descendants); false ⇒ restore. */
  archived: z.boolean().optional(),
  /** Lock / unlock against edits (needs edit access). Other changes in the same request apply after unlocking. */
  locked: z.boolean().optional(),
});
export type UpdatePageRequest = z.infer<typeof UpdatePageRequest>;
export const PageResponse = z.object({ page: PageNode });
export type PageResponse = z.infer<typeof PageResponse>;

/** The template a page belongs to: a saved page template, or a database's row template (its body page). */
export const TemplateRef = z.object({
  id: Id,
  kind: z.enum(["page", "row"]),
  name: z.string(),
  /** Row templates: their database. */
  databaseId: Id.optional(),
  /** I may edit the template (else it opens read-only). */
  canEdit: z.boolean(),
});
export type TemplateRef = z.infer<typeof TemplateRef>;

/** Open a page: metadata, breadcrumbs (root → parent), and for databases/rows the schema/views/row. */
export const GetPageResponse = z.object({
  page: PageNode,
  breadcrumbs: z.array(PageNode),
  database: Database.optional(),
  views: z.array(DbView).optional(),
  row: Row.optional(),
  /** Template content: which template this page belongs to ("editing template" mode). */
  template: TemplateRef.optional(),
});
export type GetPageResponse = z.infer<typeof GetPageResponse>;

/**
 * One-time ticket for the PageDO loro-protocol WebSocket (permission checked here, like realtime tickets).
 *
 * Page body sync (PageDO). Binary frames are loro-protocol messages (npm `loro-protocol`), i.e. exactly what the
 * official `loro-websocket` client speaks with `loro-adaptors/loro` adaptors:
 *   1. POST pageTicket → { url, access }; open `url` (single use, 60 s; a reconnect needs a NEW ticket, so create the
 *      client with `reconnect: { enabled: false }` and re-ticket on close).
 *   2. Join room `roomId = pageId` twice on the same socket:
 *      - crdt "%LOR" (LoroAdaptor over the page's LoroDoc): JoinRequest{version: doc.version().encode() | empty} →
 *        JoinResponseOk{permission, version} + a DocUpdate with what the client lacks (whole snapshot for an empty
 *        version). The client then sends what the server lacks. Local edits → DocUpdate{batchId} → Ack{refId, status}.
 *      - crdt "%EPH" (LoroEphemeralAdaptor / EphemeralStore): awareness (cursors, selections), relayed, never stored.
 *   3. `permission` is "write" for full/edit and "read" for comment/view; body DocUpdates from read-only sockets get
 *      Ack PermissionDenied (awareness is always allowed).
 *   4. Updates above ~192 KiB travel as DocUpdateFragmentHeader + DocUpdateFragment (both directions).
 *   5. Keepalive: text "ping" → "pong" (answered without waking the PageDO).
 *   6. Close 4403 "permission_changed": access changed (fatal for loro-websocket's auto-reconnect) → re-ticket.
 * Offline-first first paint: GET pageSnapshot (Loro snapshot bytes, weak ETag / 304), then join with its version.
 */
export const PageTicketResponse = z.object({ ticket: z.string(), url: z.string(), expiresAt: z.number().int(), access: PageAccess });
export type PageTicketResponse = z.infer<typeof PageTicketResponse>;

/**
 * The page body as plain blocks, for clients without a Loro runtime (Android). GET pageBlocks → the current body,
 * flat in document order (`parentId` null = top level), with `version` = the body's update counter. POST pageBlocks
 * applies block-level edits to the live Loro doc (persisted and relayed to every connected editor like any update).
 * Conflict guard per block: `base` is the block as the client last saw it (null = "didn't exist"; omitted = a new
 * block); when it no longer matches, nothing of the batch is applied and `applied` is false with the `conflicts` ids
 * and the current body. Placement: `parentId` omitted = keep (new: top level), null = top level; `afterId` null =
 * first among its siblings, omitted = keep (new: last). Text marks are the LoroText marks of the model above.
 */
const PlainMarkValue = z.union([z.boolean(), z.string(), z.number(), z.record(z.string(), z.union([z.string(), z.number(), z.boolean()]))]);
export const PlainRun = z.object({ text: z.string().max(200_000), marks: z.record(z.string(), PlainMarkValue).optional() });
export type PlainRun = z.infer<typeof PlainRun>;
export const PlainBlockContent = z.object({ type: BlockType, runs: z.array(PlainRun).max(10_000), props: BlockProps.optional() });
export type PlainBlockContent = z.infer<typeof PlainBlockContent>;
const BlockId = z.string().min(1).max(64);
export const PlainBlock = PlainBlockContent.extend({ id: BlockId, parentId: BlockId.nullable() });
export type PlainBlock = z.infer<typeof PlainBlock>;
export const PageBlocksResponse = z.object({
  version: z.number().int(),
  blocks: z.array(PlainBlock),
  /** POST only: false when a `base` didn't match (nothing applied). */
  applied: z.boolean().optional(),
  conflicts: z.array(BlockId).optional(),
});
export type PageBlocksResponse = z.infer<typeof PageBlocksResponse>;
export const PlainBlockEdit = z.discriminatedUnion("op", [
  PlainBlockContent.extend({
    op: z.literal("upsert"),
    id: BlockId,
    parentId: BlockId.nullable().optional(),
    afterId: BlockId.nullable().optional(),
    base: PlainBlockContent.nullable().optional(),
  }),
  z.object({ op: z.literal("delete"), id: BlockId, base: PlainBlockContent.nullable().optional() }),
]);
export type PlainBlockEdit = z.infer<typeof PlainBlockEdit>;
export const EditPageBlocksRequest = z.object({ ops: z.array(PlainBlockEdit).min(1).max(5_000) });
export type EditPageBlocksRequest = z.infer<typeof EditPageBlocksRequest>;

export const GetDatabaseResponse = z.object({ database: Database, views: z.array(DbView) });
export type GetDatabaseResponse = z.infer<typeof GetDatabaseResponse>;

/**
 * Replace the property list (server diffs by id: new ids added, missing ids deleted, others updated). Cells that no
 * longer fit (removed property, type or relation target changed, removed option) are dropped without row events:
 * clients apply the same rule to cached rows on `database.updated`. Views stop referencing removed properties
 * (`view.updated`). A relation with `inversePropertyId` is two-way (the server creates the inverse property).
 */
export const UpdateDatabaseRequest = z.object({
  title: z.string().max(2000).optional(),
  icon: z.string().max(64).optional(),
  properties: z.array(PropertyDef).optional(),
  /** The row template "New" uses; null ⇒ none (an empty row). */
  defaultRowTemplateId: Id.nullable().optional(),
});
export type UpdateDatabaseRequest = z.infer<typeof UpdateDatabaseRequest>;
export const DatabaseResponse = z.object({ database: Database });
export type DatabaseResponse = z.infer<typeof DatabaseResponse>;

/** Rows are returned in rank order; filtering/sorting/grouping is client-side (packages/core logic, translated). */
export const ListRowsQuery = z.object({ cursor: z.string().optional(), limit: z.coerce.number().int().min(1).max(1000).default(500) });
export const ListRowsResponse = z.object({ rows: z.array(Row), nextCursor: z.string().optional() });
export type ListRowsResponse = z.infer<typeof ListRowsResponse>;

export const CreateRowRequest = z.object({
  values: z.record(Id, PropertyValue).optional(),
  afterId: Id.optional(),
  clientId: z.string().max(64).optional(),
  /**
   * A row template of this database: its default cells fill in what `values` leaves out (core
   * `logic/templates#applyRowTemplate`, same on every platform) and its body is copied into the new row's page.
   */
  templateId: Id.optional(),
});
export type CreateRowRequest = z.infer<typeof CreateRowRequest>;
export const RowResponse = z.object({ row: Row });
export type RowResponse = z.infer<typeof RowResponse>;

/**
 * Partial update: listed cells are replaced; `null` clears a cell. `afterId`/`beforeId` reorder (board drag): after
 * afterId and/or before beforeId; `afterId: null` ⇒ first, `beforeId: null` ⇒ last. Two-way relation cells update
 * the related rows too (row.updated for each).
 */
export const UpdateRowRequest = z.object({
  values: z.record(Id, PropertyValue.nullable()).optional(),
  afterId: Id.nullable().optional(),
  beforeId: Id.nullable().optional(),
  icon: z.string().max(64).optional(),
});
export type UpdateRowRequest = z.infer<typeof UpdateRowRequest>;

export const CreateViewRequest = DbView.omit({ id: true, databaseId: true, rank: true }).extend({ clientId: z.string().max(64).optional() });
export type CreateViewRequest = z.infer<typeof CreateViewRequest>;
/** Partial update: absent fields are kept; `null` clears group-by / date property / filter (board and calendar still need theirs). */
export const UpdateViewRequest = DbView.omit({ id: true, databaseId: true, rank: true })
  .partial()
  .extend({
    afterId: Id.nullable().optional(),
    groupByPropertyId: Id.nullable().optional(),
    datePropertyId: Id.nullable().optional(),
    filter: ViewFilter.nullable().optional(),
  });
export type UpdateViewRequest = z.infer<typeof UpdateViewRequest>;
export const ViewResponse = z.object({ view: DbView });

/**
 * Preview a formula while editing (every platform: clients don't run the engine). `expression` is typed text (or the
 * stored form); the result is for `rowId` (default: the first row). `display` is the expression as clients show it;
 * `error` explains a parse error or a limit (a limit also makes saving fail with the same message).
 */
export const FormulaPreviewRequest = z.object({
  propertyId: Id.optional(),
  expression: z.string().max(8000),
  rowId: Id.optional(),
  format: FormulaDef.shape.format,
});
export type FormulaPreviewRequest = z.infer<typeof FormulaPreviewRequest>;
export const FormulaPreviewResponse = z.object({
  result: FormulaResult.optional(),
  resultKind: FormulaResultKind.optional(),
  expression: z.string(),
  display: z.string(),
  error: z.string().optional(),
});
export type FormulaPreviewResponse = z.infer<typeof FormulaPreviewResponse>;
export type ViewResponse = z.infer<typeof ViewResponse>;

// ---------- Sheet view (Phase 2) ----------

/** Cells per batch write (a fill or paste); bigger batches are refused with the count. */
export const SHEET_MAX_BATCH_CELLS = 10_000;

/**
 * One cell write. Target: `rowId` (a data row) or `summaryRowId`, and `propertyId`. Content, exactly one of:
 * - `value`: a typed value for the column (null clears; data rows only — summary cells take null to clear);
 * - `expression`: formula text as typed (A1, `$`, `'Sheet'!A1`, leading "=" optional) or the stored form;
 * - `text`: what someone typed or pasted: "=…" is a formula, anything else is parsed for the column's type
 *   (core `cellInputValue`); summary cells keep it as text.
 */
export const CellWrite = z.object({
  rowId: Id.optional(),
  summaryRowId: Id.optional(),
  propertyId: Id,
  value: PropertyValue.nullable().optional(),
  expression: z.string().max(8000).optional(),
  text: z.string().max(10_000).optional(),
});
export type CellWrite = z.infer<typeof CellWrite>;
/** Applied atomically as one change (all or nothing). */
export const WriteCellsRequest = z.object({ writes: z.array(CellWrite).min(1).max(100_000) });
export type WriteCellsRequest = z.infer<typeof WriteCellsRequest>;
/** Rows whose cells changed (after recalculation) and the database (summary rows with their results). */
export const WriteCellsResponse = z.object({ rows: z.array(Row), database: Database });
export type WriteCellsResponse = z.infer<typeof WriteCellsResponse>;

/** Insert a summary row at `index` (0 = first; default: last) with optional cells (same content rules as CellWrite). */
export const CreateSummaryRowRequest = z.object({
  index: z.number().int().min(0).optional(),
  cells: z.record(Id, z.object({ expression: z.string().max(8000).optional(), text: z.string().max(10_000).optional() })).optional(),
});
export type CreateSummaryRowRequest = z.infer<typeof CreateSummaryRowRequest>;
/** Move a summary row to `index` (0 = first). */
export const UpdateSummaryRowRequest = z.object({ index: z.number().int().min(0) });
export type UpdateSummaryRowRequest = z.infer<typeof UpdateSummaryRowRequest>;
export const SummaryRowResponse = z.object({ summaryRow: SummaryRow.optional(), database: Database });
export type SummaryRowResponse = z.infer<typeof SummaryRowResponse>;

/**
 * "Sort range": reorders the database's rows for everyone (rewrites the manual order) by a property, as core
 * `sortRows` sorts. `previousOrder` (row ids in the old manual order) is what Undo sends back to reorderRows.
 */
export const SortRowsRequest = z.object({ propertyId: Id, direction: z.enum(["asc", "desc"]) });
export type SortRowsRequest = z.infer<typeof SortRowsRequest>;
/** An explicit manual order (Undo of a sort): every live row id once; ids not listed keep their order after them. */
export const ReorderRowsRequest = z.object({ order: z.array(Id).max(25_000) });
export type ReorderRowsRequest = z.infer<typeof ReorderRowsRequest>;
export const ReorderRowsResponse = z.object({ previousOrder: z.array(Id), order: z.array(Id) });
export type ReorderRowsResponse = z.infer<typeof ReorderRowsResponse>;
